import { logger } from '@/core/logger';
import { secureStorage } from './secureStorage';

/**
 * Storage keys for passcode state
 */
const STORAGE_KEYS = {
    IS_SET: 'passcode.isSet',
    BIOMETRIC_ENABLED: 'passcode.biometricEnabled',
    PASSCODE: 'passcode.value', // Store passcode for biometric login (encrypted)
    SETUP_REQUIRED: 'passcode.setupRequired', // Flag to indicate passcode setup is required (from login response)
} as const;

/**
 * Passcode storage management
 * Handles secure storage of passcode state (never stores actual passcode)
 */
export class PasscodeStorage {
    /**
     * Check if passcode is set
     */
    static async isPasscodeSet(): Promise<boolean> {
        try {
            const value = await secureStorage.getItem(STORAGE_KEYS.IS_SET);
            return value === 'true';
        } catch (error) {
            logger.error('PasscodeStorage:Failed to check passcode state', error);
            return false;
        }
    }

    /**
     * Set passcode state (passcode is set)
     */
    static async setPasscodeState(biometricEnabled: boolean = false): Promise<void> {
        try {
            await secureStorage.setItem(STORAGE_KEYS.IS_SET, 'true');
            await secureStorage.setItem(STORAGE_KEYS.BIOMETRIC_ENABLED, biometricEnabled ? 'true' : 'false');
            logger.info('PasscodeStorage:Passcode state set', { biometricEnabled });
        } catch (error) {
            logger.error('PasscodeStorage:Failed to set passcode state', error);
            throw new Error('Failed to save passcode state');
        }
    }

    /**
     * Clear passcode state
     */
    static async clearPasscodeState(): Promise<void> {
        try {
            await secureStorage.removeItem(STORAGE_KEYS.IS_SET);
            await secureStorage.removeItem(STORAGE_KEYS.BIOMETRIC_ENABLED);
            await secureStorage.removeItem(STORAGE_KEYS.PASSCODE);
            await secureStorage.removeItem(STORAGE_KEYS.SETUP_REQUIRED);
            logger.info('PasscodeStorage:Passcode state cleared');
        } catch (error) {
            logger.error('PasscodeStorage:Failed to clear passcode state', error);
            throw new Error('Failed to clear passcode state');
        }
    }

    /**
     * Check if passcode setup is required (from login response)
     */
    static async isSetupRequired(): Promise<boolean> {
        try {
            const value = await secureStorage.getItem(STORAGE_KEYS.SETUP_REQUIRED);
            return value === 'true';
        } catch (error) {
            logger.error('PasscodeStorage:Failed to check setup required state', error);
            return false;
        }
    }

    /**
     * Set passcode setup required flag (when login response indicates passcode not set)
     */
    static async setSetupRequired(): Promise<void> {
        try {
            await secureStorage.setItem(STORAGE_KEYS.SETUP_REQUIRED, 'true');
            logger.info('PasscodeStorage:Setup required flag set');
        } catch (error) {
            logger.error('PasscodeStorage:Failed to set setup required flag', error);
            throw new Error('Failed to save setup required flag');
        }
    }

    /**
     * Clear passcode setup required flag (when passcode is successfully set)
     */
    static async clearSetupRequired(): Promise<void> {
        try {
            await secureStorage.removeItem(STORAGE_KEYS.SETUP_REQUIRED);
            logger.info('PasscodeStorage:Setup required flag cleared');
        } catch (error) {
            logger.error('PasscodeStorage:Failed to clear setup required flag', error);
        }
    }

    /**
     * Get biometric enabled state
     */
    static async getBiometricEnabled(): Promise<boolean> {
        try {
            const value = await secureStorage.getItem(STORAGE_KEYS.BIOMETRIC_ENABLED);
            return value === 'true';
        } catch (error) {
            logger.error('PasscodeStorage:Failed to get biometric state', error);
            return false;
        }
    }

    /**
     * Set biometric enabled state
     */
    static async setBiometricEnabled(enabled: boolean): Promise<void> {
        try {
            await secureStorage.setItem(STORAGE_KEYS.BIOMETRIC_ENABLED, enabled ? 'true' : 'false');
            logger.info('PasscodeStorage:Biometric state updated', { enabled });
        } catch (error) {
            logger.error('PasscodeStorage:Failed to set biometric state', error);
            throw new Error('Failed to update biometric state');
        }
    }

    /**
     * Store passcode for biometric login
     * Only called when biometric is enabled
     */
    static async setPasscode(passcode: string): Promise<void> {
        try {
            await secureStorage.setItem(STORAGE_KEYS.PASSCODE, passcode);
            logger.info('PasscodeStorage:Passcode stored for biometric');
        } catch (error) {
            logger.error('PasscodeStorage:Failed to store passcode', error);
            throw new Error('Failed to store passcode');
        }
    }

    /**
     * Get stored passcode for biometric login
     */
    static async getPasscode(): Promise<string | null> {
        try {
            return await secureStorage.getItem(STORAGE_KEYS.PASSCODE);
        } catch (error) {
            logger.error('PasscodeStorage:Failed to get passcode', error);
            return null;
        }
    }

    /**
     * Clear stored passcode
     */
    static async clearPasscode(): Promise<void> {
        try {
            await secureStorage.removeItem(STORAGE_KEYS.PASSCODE);
            logger.info('PasscodeStorage:Passcode cleared');
        } catch (error) {
            logger.error('PasscodeStorage:Failed to clear passcode', error);
        }
    }
}

export const passcodeStorage = PasscodeStorage;

